import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { playClick } from "../lib/sounds";
import { Button } from "./Button";
import { Dropdown } from "./Dropdown";
import { closeModal } from "./globalModalStack";
import { Icon, type IconName } from "./Icon";
import { Modal } from "./Modal";
import {
  MODAL_CARD_SHELL_STYLE,
  ModalContent,
  ModalFooter,
  ModalHead,
} from "./ModalCard";
import { PreviewListBox } from "./PreviewList";
import {
  DEFAULT_NAV_CONFIG,
  moveWithinSection,
  NAV_SECTIONS,
  type NavConfigEntry,
  type NavItemKey,
  type NavRule,
  type NavSection,
  resolveNavConfig,
  sectionEntries,
  serializeNavConfig,
} from "./navItems";
import { PillBadge } from "./Settings/shared";
import { Text } from "./Text";

const RULE_LABELS: Record<NavRule, string> = {
  always: "Always show",
  "when-badged": "Show when badged",
  hidden: "Hidden",
};

const RULE_ICONS: Record<NavRule, IconName> = {
  always: "eye",
  "when-badged": "bell",
  hidden: "eye-closed",
};

const SECTION_LABELS: Record<NavSection, string> = {
  main: "Personal",
  manage: "Tenant",
};

function rulesFor(hasBadge: boolean): NavRule[] {
  return hasBadge ? ["always", "when-badged", "hidden"] : ["always", "hidden"];
}

const SectionGroup = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SectionLabel = styled("div")`
  padding: 0 8px;
`;

const Row = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  border-radius: 8px;
  min-width: 0;
  cursor: grab;
  user-select: none;
  touch-action: none;
  &:hover {
    background: var(--alpha4);
  }
  &[data-ghost="true"] {
    background: var(--alpha4);
  }
  &[data-ghost="true"] > * {
    opacity: 0;
  }
`;

const Label = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

const RuleSlot = styled("div")`
  flex-shrink: 0;
`;

const FloatingRow = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  box-sizing: border-box;
  background: var(--surface-primary);
  border-radius: 8px;
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  cursor: grabbing;
`;

function useRowFlip(
  order: ReadonlyArray<NavConfigEntry>,
  rowRefs: React.MutableRefObject<Map<NavItemKey, HTMLElement>>,
  snapshotsRef: React.MutableRefObject<Map<NavItemKey, DOMRect>>,
  active: boolean
) {
  useLayoutEffect(() => {
    if (!active) {
      for (const el of rowRefs.current.values()) {
        el.style.transform = "";
        el.style.transition = "";
      }
      snapshotsRef.current.clear();
      return;
    }
    const snapshots = snapshotsRef.current;
    if (snapshots.size === 0) return;
    for (const { item } of order) {
      const el = rowRefs.current.get(item.key);
      if (!el) continue;
      const prev = snapshots.get(item.key);
      if (!prev) continue;
      el.style.transition = "none";
      el.style.transform = "";
      const layout = el.getBoundingClientRect();
      const dx = prev.left - layout.left;
      const dy = prev.top - layout.top;
      if (dx === 0 && dy === 0) continue;
      el.style.transform = `translate(${dx}px, ${dy}px)`;
      el.getBoundingClientRect();
      el.style.transition = "transform 200ms ease";
      el.style.transform = "";
    }
    snapshots.clear();
  }, [order, rowRefs, snapshotsRef, active]);
}

export function CustomizeNavigationModal() {
  const z = useZero();
  const [preferences] = useQuery(queries.userPreferences());
  const entries = resolveNavConfig(preferences?.navConfig);

  const save = useCallback(
    (next: ReadonlyArray<NavConfigEntry>) => {
      z.mutate(
        sharedMutators.setUserPreference({
          navConfig: serializeNavConfig(next),
        })
      );
    },
    [z]
  );

  const [draggedKey, setDraggedKey] = useState<NavItemKey | null>(null);
  const [dropIndex, setDropIndex] = useState(-1);
  const [dropping, setDropping] = useState(false);

  const rowRefs = useRef<Map<NavItemKey, HTMLElement>>(new Map());
  const dragStart = useRef({ x: 0, y: 0 });
  const [originRect, setOriginRect] = useState<DOMRect | null>(null);
  const rowCenters = useRef<{ y: number; index: number }[]>([]);
  const floatingRef = useRef<HTMLDivElement>(null);
  const draggedKeyRef = useRef<NavItemKey | null>(null);
  const sectionLenRef = useRef(0);
  const dropIndexRef = useRef(-1);
  const initialDropIdx = useRef(-1);
  const flipSnapshots = useRef<Map<NavItemKey, DOMRect>>(new Map());

  const entriesRef = useRef(entries);
  const saveRef = useRef(save);
  useEffect(() => {
    entriesRef.current = entries;
    saveRef.current = save;
  });

  const setRowRef = useCallback((key: NavItemKey, el: HTMLElement | null) => {
    if (el) rowRefs.current.set(key, el);
    else rowRefs.current.delete(key);
  }, []);

  const handlePointerDown = useCallback(
    (e: React.PointerEvent, key: NavItemKey) => {
      if (e.button !== 0) return;
      const current = entriesRef.current;
      const entry = current.find((x) => x.item.key === key);
      const el = rowRefs.current.get(key);
      if (!entry || !el) return;

      playClick();
      dragStart.current = { x: e.clientX, y: e.clientY };
      setOriginRect(el.getBoundingClientRect());

      const siblings = sectionEntries(current, entry.item.section);
      sectionLenRef.current = siblings.length;
      rowCenters.current = siblings
        .filter((x) => x.item.key !== key)
        .map((x, i) => {
          const ref = rowRefs.current.get(x.item.key);
          if (!ref) return { y: 0, index: i };
          const rect = ref.getBoundingClientRect();
          return { y: rect.top + rect.height / 2, index: i };
        });

      const idx = siblings.findIndex((x) => x.item.key === key);
      draggedKeyRef.current = key;
      dropIndexRef.current = idx;
      initialDropIdx.current = idx;
      setDropIndex(idx);
      setDraggedKey(key);
    },
    []
  );

  useEffect(() => {
    if (!draggedKey) return;

    const onMove = (e: PointerEvent) => {
      if (!draggedKeyRef.current || !originRect) return;

      if (floatingRef.current) {
        const dx = e.clientX - dragStart.current.x;
        const dy = e.clientY - dragStart.current.y;
        floatingRef.current.style.left = `${originRect.left + dx}px`;
        floatingRef.current.style.top = `${originRect.top + dy}px`;
      }

      if (rowCenters.current.length === 0) return;

      const cy = e.clientY;
      let nearestI = rowCenters.current[0]!.index;
      let nearestDist = Number.POSITIVE_INFINITY;
      let nearestCenter = rowCenters.current[0]!;
      for (const c of rowCenters.current) {
        const d = Math.abs(cy - c.y);
        if (d < nearestDist) {
          nearestDist = d;
          nearestI = c.index;
          nearestCenter = c;
        }
      }
      let idx = nearestI;
      if (cy > nearestCenter.y) idx = nearestI + 1;
      const maxIdx = sectionLenRef.current - 1;
      if (idx > maxIdx) idx = maxIdx;
      if (idx < 0) idx = 0;

      if (idx !== dropIndexRef.current) {
        const snapshots = new Map<NavItemKey, DOMRect>();
        for (const [k, el] of rowRefs.current) {
          snapshots.set(k, el.getBoundingClientRect());
        }
        flipSnapshots.current = snapshots;
        dropIndexRef.current = idx;
        setDropIndex(idx);
        playClick();
      }
    };

    const onUp = () => {
      if (!draggedKeyRef.current) return;
      const draggedEl = rowRefs.current.get(draggedKeyRef.current);
      const el = floatingRef.current;
      if (draggedEl && el) {
        const rect = draggedEl.getBoundingClientRect();
        el.getBoundingClientRect();
        el.style.transition = "left 0.2s ease, top 0.2s ease";
        el.style.left = `${rect.left}px`;
        el.style.top = `${rect.top}px`;
      }
      draggedKeyRef.current = null;
      setDropping(true);
    };

    const onCancel = () => {
      if (!draggedKeyRef.current) return;
      draggedKeyRef.current = null;
      setOriginRect(null);
      dropIndexRef.current = -1;
      initialDropIdx.current = -1;
      flipSnapshots.current.clear();
      setDraggedKey(null);
      setDropIndex(-1);
      setDropping(false);
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onCancel);
    return () => {
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onCancel);
    };
  }, [draggedKey, originRect]);

  useEffect(() => {
    if (!dropping) return;
    const timer = setTimeout(() => {
      if (draggedKey && dropIndex !== initialDropIdx.current) {
        saveRef.current(
          moveWithinSection(entriesRef.current, draggedKey, dropIndex)
        );
      }
      playClick();
      setOriginRect(null);
      dropIndexRef.current = -1;
      initialDropIdx.current = -1;
      setDraggedKey(null);
      setDropIndex(-1);
      setDropping(false);
    }, 200);
    return () => clearTimeout(timer);
  }, [dropping, draggedKey, dropIndex]);

  const visualOrder =
    draggedKey && dropIndex >= 0
      ? moveWithinSection(entries, draggedKey, dropIndex)
      : entries;
  useRowFlip(visualOrder, rowRefs, flipSnapshots, !!draggedKey);

  const draggedEntry = draggedKey
    ? entries.find((entry) => entry.item.key === draggedKey)
    : null;

  const setRule = (key: NavItemKey, rule: NavRule) => {
    save(
      entries.map((entry) =>
        entry.item.key === key ? { item: entry.item, rule } : entry
      )
    );
  };

  const isDefault =
    !preferences?.navConfig || preferences.navConfig === DEFAULT_NAV_CONFIG;

  return (
    <Modal
      open
      onClose={closeModal}
      style={{ ...MODAL_CARD_SHELL_STYLE, width: 320 }}
    >
      <ModalContent>
        <ModalHead>
          <Text variant="text-regular-md" color="var(--text-primary)">
            Customize sidebar
          </Text>
          <Text variant="text-regular" color="var(--text-secondary)">
            Drag to reorder items within a section, and choose when each one
            appears.
          </Text>
        </ModalHead>

        {NAV_SECTIONS.map((section) => (
          <SectionGroup key={section}>
            <SectionLabel>
              <Text variant="text-small" color="var(--text-secondary)">
                {SECTION_LABELS[section]}
              </Text>
            </SectionLabel>
            <PreviewListBox style={{ maxHeight: "none", overflow: "visible" }}>
              {sectionEntries(visualOrder, section).map(({ item, rule }) => {
                const isDragged = item.key === draggedKey;
                return (
                  <Row
                    key={item.key}
                    ref={(el) => setRowRef(item.key, el)}
                    data-ghost={isDragged || undefined}
                    onPointerDown={(e) => handlePointerDown(e, item.key)}
                    style={{ cursor: draggedKey ? "grabbing" : undefined }}
                  >
                    <Label
                      style={{
                        opacity:
                          rule === "hidden" && !isDragged ? 0.4 : undefined,
                      }}
                    >
                      <Icon name={item.icon} size={16} />
                      <Text ellipsis>{item.label}</Text>
                    </Label>
                    <RuleSlot onPointerDown={(e) => e.stopPropagation()}>
                      <Dropdown
                        align="bottom-left"
                        minWidth={168}
                        opener={
                          <PillBadge
                            icon={RULE_ICONS[rule]}
                            interactive
                            style={{ flexShrink: 0 }}
                          >
                            {RULE_LABELS[rule]}
                          </PillBadge>
                        }
                        sections={[
                          {
                            items: rulesFor(item.hasBadge).map((r) => ({
                              type: "button" as const,
                              left: <Icon name={RULE_ICONS[r]} size={16} />,
                              label: RULE_LABELS[r],
                              right:
                                r === rule ? (
                                  <Icon
                                    name="check"
                                    size={12}
                                    color="var(--text-secondary)"
                                  />
                                ) : undefined,
                              onClick: () => setRule(item.key, r),
                            })),
                          },
                        ]}
                      />
                    </RuleSlot>
                  </Row>
                );
              })}
            </PreviewListBox>
          </SectionGroup>
        ))}

        <ModalFooter>
          {!isDefault && (
            <Button
              size="l"
              variant="subtle"
              onClick={() =>
                z.mutate(sharedMutators.setUserPreference({ navConfig: null }))
              }
            >
              Reset to default
            </Button>
          )}
          <Button size="l" variant="primary" onClick={closeModal}>
            Done
          </Button>
        </ModalFooter>
      </ModalContent>

      {draggedEntry && originRect && (
        <div
          ref={floatingRef}
          style={{
            position: "fixed",
            left: originRect.left,
            top: originRect.top,
            width: originRect.width,
            zIndex: 1000,
            pointerEvents: "none",
          }}
        >
          <FloatingRow>
            <Icon name={draggedEntry.item.icon} size={16} />
            <Text>{draggedEntry.item.label}</Text>
          </FloatingRow>
        </div>
      )}
    </Modal>
  );
}
